import React, { useEffect, useMemo, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import AcceptRequestModal from "../../components/models/AcceptRequestModal";
import DeclineRequestModal from "../../components/models/DeclineRequestModal";
import { HiSearch } from "react-icons/hi";
import apiService from "../../actions/apiService";
const STATUS_PILL = {
  listed: "bg-[#8794E0] text-[#FFFFFF]",
  pending: "bg-[#8794E0] text-[#FFFFFF]",
  accepted: "bg-[#B9FFCC] text-[#6C8F75]",
  rejected: "bg-[#FFA9A2] text-[#840000]",
};

// Format status labels for this page
const formatStatusLabel = (status) => {
  if (!status) return "";
  const normalized = String(status).toLowerCase();
  if (normalized === "pending") {
    return "Offer received";
  }
  if (normalized === "accepted") {
    return "Offer accepted";
  }
  if (normalized === "rejected") {
    return "Offer rejected";
  }
  if (
    normalized === "listed" ||
    normalized === "available" ||
    normalized === "active"
  ) {
    return "Listed";
  }
  return status;
};

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Offer received" },
  { key: "accepted", label: "Offer accepted" },
  { key: "rejected", label: "Offer rejected" },
];

// Helper to map API request -> table row
const mapRequestToRow = (req) => ({
  id: req.id,
  buyer: {
    name:
      `${req?.buyer?.firstName || ""} ${req?.buyer?.lastName || ""}`.trim() ||
      "—",
    email: req?.buyer?.email || "—",
    avatar: req?.buyer?.profile_image_url,
  },
  listing: {
    title: req?.item?.title || "—",
    type: req?.item?.custom_category
      ? `${req?.item?.custom_category}`
      : req?.item?.category || "—",
    thumb: req?.item?.images?.[0]?.file_url,
  },
  offer: req?.offer_amount != null ? String(req.offer_amount) : "—",
  message: req?.message || "—",
  status: req?.status || "pending",
});

const SellerRequests = () => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [activeTab, setActiveTab] = useState("all");
  const [counts, setCounts] = useState({
    all: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
  });
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rowsData, setRowsData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notifyingBuyerId, setNotifyingBuyerId] = useState(null);
  const [notifiedBuyerId, setNotifiedBuyerId] = useState(null);

  // Helper: refresh counters from backend
  const updateCounts = async () => {
    const fetchCount = async (st) => {
      const res = await apiService.get("/requests", {
        role: "seller",
        status: st !== "all" ? st : undefined,
        page: 1,
        limit: 1,
      });
      const total =
        res?.data?.pagination?.total ??
        (Array.isArray(res?.data?.requests) ? res.data.requests.length : 0);
      return total;
    };
    const [all, pending, accepted, rejected] = await Promise.all([
      fetchCount("all"),
      fetchCount("pending"),
      fetchCount("accepted"),
      fetchCount("rejected"),
    ]);
    setCounts({ all, pending, accepted, rejected });
  };

  // Fetch seller requests: backend handles status filtering
  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setError(null);
      setIsLoading(true);
      try {
        const res = await apiService.get("/requests", {
          role: "seller",
          status: activeTab !== "all" ? activeTab : undefined,
          search: search || undefined,
          page: 1,
          limit: 100,
          sort: "created_at",
          order: sort === "recent" ? "desc" : "asc",
        });
        if (!active) return;
        const list = res?.data?.requests || res?.data || [];
        setRowsData(Array.isArray(list) ? list.map(mapRequestToRow) : []);
      } catch (e) {
        if (active)
          setError(typeof e === "string" ? e : "Failed to load requests");
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [search, activeTab, sort]);

  // Fetch counters per status from backend
  useEffect(() => {
    const run = async () => {
      try {
        await updateCounts();
      } catch (_) {
        /* ignore counters failure */
      }
    };
    run();
  }, []);

  const rows = useMemo(() => {
    let r = [...rowsData];
    if (search.trim()) {
      r = r.filter(
        (x) =>
          x.buyer.name.toLowerCase().includes(search.toLowerCase()) ||
          x.listing.title.toLowerCase().includes(search.toLowerCase())
      );
    }
    return r;
  }, [rowsData, search]);

  const handleAcceptRequest = (request) => {
    setSelectedRequest(request);
    setAcceptModalOpen(true);
  };

  const handleDeclineRequest = (request) => {
    setSelectedRequest(request);
    setDeclineModalOpen(true);
  };

  const handleNotifyBuyer = async (request) => {
    if (!request?.id || !["accepted", "rejected"].includes(request.status)) {
      return;
    }
    setNotifyingBuyerId(request.id);
    setNotifiedBuyerId(null);
    try {
      const res = await apiService.post(
        `/requests/${request.id}/notify-buyer`,
        {}
      );
      if (res?.success) {
        setNotifiedBuyerId(request.id);
        setTimeout(() => setNotifiedBuyerId(null), 2000);
      }
    } catch (_) {
      /* ignore for now */
    } finally {
      setNotifyingBuyerId(null);
    }
  };

  const handleConfirmAccept = async () => {
    if (!selectedRequest?.id) return;
    try {
      await apiService.patch(`/requests/${selectedRequest.id}/status`, {
        action: "accept",
      });
      setAcceptModalOpen(false);
      setSelectedRequest(null);
      // Refresh list
      const res = await apiService.get("/requests", {
        role: "seller",
        status: activeTab !== "all" ? activeTab : undefined,
        search: search || undefined,
        page: 1,
        limit: 100,
      });
      const list = res?.data?.requests || res?.data || [];
      setRowsData(Array.isArray(list) ? list.map(mapRequestToRow) : []);
      // Refresh counters
      await updateCounts();
    } catch (e) {
      // Silent fail for now
      setAcceptModalOpen(false);
      setSelectedRequest(null);
    }
  };

  const handleConfirmDecline = async () => {
    if (!selectedRequest?.id) return;
    try {
      await apiService.patch(`/requests/${selectedRequest.id}/status`, {
        action: "reject",
        reject_reason: "Declined by seller",
      });
      setDeclineModalOpen(false);
      setSelectedRequest(null);
      const res = await apiService.get("/requests", {
        role: "seller",
        status: activeTab !== "all" ? activeTab : undefined,
        search: search || undefined,
        page: 1,
        limit: 100,
      });
      const list = res?.data?.requests || res?.data || [];
      setRowsData(Array.isArray(list) ? list.map(mapRequestToRow) : []);
      // Refresh counters
      await updateCounts();
    } catch (e) {
      setDeclineModalOpen(false);
      setSelectedRequest(null);
    }
  };

  const handleCloseAcceptModal = () => {
    setAcceptModalOpen(false);
    setSelectedRequest(null);
  };

  const handleCloseDeclineModal = () => {
    setDeclineModalOpen(false);
    setSelectedRequest(null);
  };

  return (
    <LayoutBuyerSeller>
      {/* Header */}
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative mb-0 md:mb-8 z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Buyers requests
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Find all of your buyers requests below.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-8">
          <div className="w-full bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] p-5 pt-[40px] md:pt-5 grid md:flex items-center gap-4">
            <div className="relative flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 rounded-[8px] border border-gray-200 pl-9 pr-[12px] text-caption bg-white"
                placeholder="Search…"
              />
              <HiSearch className="absolute left-3 top-3.5 text-gray-400 h-4 w-4" />
            </div>
            <div className="inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M3.88589 5.66687C3.12955 4.82155 2.75138 4.39889 2.73712 4.03968C2.72473 3.72764 2.85882 3.42772 3.09963 3.22889C3.37684 3 3.94399 3 5.07828 3H19.9212C21.0555 3 21.6227 3 21.8999 3.22889C22.1407 3.42772 22.2748 3.72764 22.2624 4.03968C22.2481 4.39889 21.8699 4.82155 21.1136 5.66687L15.4074 12.0444C15.2566 12.2129 15.1812 12.2972 15.1275 12.3931C15.0798 12.4781 15.0448 12.5697 15.0236 12.6648C14.9997 12.7721 14.9997 12.8852 14.9997 13.1113V18.4584C14.9997 18.6539 14.9997 18.7517 14.9682 18.8363C14.9403 18.911 14.895 18.9779 14.836 19.0315C14.7692 19.0922 14.6784 19.1285 14.4969 19.2012L11.0969 20.5612C10.7293 20.7082 10.5455 20.7817 10.398 20.751C10.269 20.7242 10.1558 20.6476 10.083 20.5377C9.99975 20.4122 9.99975 20.2142 9.99975 19.8184V13.1113C9.99975 12.8852 9.99975 12.7721 9.97587 12.6648C9.95469 12.5697 9.91971 12.4781 9.87204 12.3931C9.81828 12.2972 9.7429 12.2129 9.59213 12.0444L3.88589 5.66687Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="all">All status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
              <span aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="18"
                  viewBox="0 0 21 18"
                  fill="none"
                >
                  <path
                    d="M15.5 1V17M15.5 17L11.5 13M15.5 17L19.5 13M5.5 17V1M5.5 1L1.5 5M5.5 1L9.5 5"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent w-full focus:outline-none text-sm"
              >
                <option value="recent">Most recent</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + Table merged in one container */}
      <div className="bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-[12px] overflow-hidden">
        {error && (
          <div className="p-4 text-sm text-red-600">{String(error)}</div>
        )}
        {/* {isLoading && (
          <div className="p-4 text-sm text-gray-500">Loading requests…</div>
        )} */}
        {/* Tabs row */}
        <div className="p-5 pb-8 flex items-center gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-[8.5px] rounded-[8px] text-[14px] whitespace-nowrap transition flex items-center w-fit min-w-fit max-w-fit gap-4 border border-esBorderLightBlur ${
                activeTab === t.key ? "bg-esBorderLightBlur text-[#2A8BF7]" : ""
              }`}
            >
              <span>{t.label}</span>
              <span
                className={`${
                  activeTab === t.key
                    ? "bg-white text-[#575757]"
                    : "bg-[#E0E0E0] text-[#575757]"
                } rounded-[50px] px-[10px] py-1 text-[14px] `}
              >
                {t.key === "all" ? counts.all : counts[t.key] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        {/* Mobile cards */}
        <div className="md:hidden px-4 pb-5 space-y-4">
          {isLoading && rows.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">
              Loading requests…
            </div>
          )}
          {!isLoading && rows.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">
              No requests found
            </div>
          )}
          {rows.map((row) => (
            <div
              key={row.id}
              className="bg-white rounded-[12px] border border-[#DCEDFF] p-4 space-y-3"
            >
              {/* Header: buyer */}
              <div className="flex items-center border-b border-[rgba(30,30,30,0.25)] pb-4 gap-3">
                <img
                  src={row.buyer.avatar}
                  alt="avatar"
                  className="h-9 w-9 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-esdarkGray opacity-75 text-[14px] truncate">
                    {row.buyer.name}
                  </div>
                  <div className="text-[12px] opacity-50 text-esdarkGray truncate">
                    {row.buyer.email}
                  </div>
                </div>
              </div>

              {/* Listing */}
              <div className="flex items-start gap-3 pt-2">
                <div className="h-12 w-12 bg-gray-100 rounded overflow-hidden shrink-0">
                  <img
                    src={row?.listing?.thumb}
                    alt="thumb"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium text-esdarkGray truncate">
                    {row.listing.title}
                  </div>
                  <div className="text-[12px] text-[#000025] opacity-75">
                    {row.listing.type}
                  </div>
                </div>
                <span
                  className={`text-[12px] px-2 py-1 rounded-full whitespace-nowrap ${
                    STATUS_PILL[row.status] || STATUS_PILL.listed
                  }`}
                >
                  {formatStatusLabel(row.status)}
                </span>
              </div>

              {/* Offer */}
              <div className="text-[#000025] text-[20px] font-semibold">
                {row.offer}
              </div>

              {/* Message */}
              <div className="text-[12px] text-[#000025] opacity-50">
                {row.message}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-1">
                {row.status === "pending" && (
                  <>
                    <button
                      onClick={() => handleAcceptRequest(row)}
                      title="Approve"
                      className="h-9 w-9 flex items-center justify-center rounded-[8px] bg-[#B9FFCC] text-[#1E1E1E]"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="13"
                        viewBox="0 0 18 13"
                        fill="none"
                      >
                        <path
                          d="M17 1L6 12L1 7"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeclineRequest(row)}
                      title="Reject"
                      className="h-9 w-9 flex items-center justify-center rounded-[8px] bg-[#FFD9D9] text-[#FF6E6E]"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M13 1L1 13M1 1L13 13"
                          stroke="#FF6E6E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </>
                )}
                <button
                  title="Message"
                  className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-[8px] bg-[#FFFFFF] text-[#1E1E1E] opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="18"
                    viewBox="0 0 22 18"
                    fill="none"
                  >
                    <path
                      d="M20.5 15L13.8571 9M8.14286 9L1.50003 15M1 4L9.16492 9.71544C9.82609 10.1783 10.1567 10.4097 10.5163 10.4993C10.8339 10.5785 11.1661 10.5785 11.4837 10.4993C11.8433 10.4097 12.1739 10.1783 12.8351 9.71544L21 4M5.8 17H16.2C17.8802 17 18.7202 17 19.362 16.673C19.9265 16.3854 20.3854 15.9265 20.673 15.362C21 14.7202 21 13.8802 21 12.2V5.8C21 4.11984 21 3.27976 20.673 2.63803C20.3854 2.07354 19.9265 1.6146 19.362 1.32698C18.7202 1 17.8802 1 16.2 1H5.8C4.11984 1 3.27976 1 2.63803 1.32698C2.07354 1.6146 1.6146 2.07354 1.32698 2.63803C1 3.27976 1 4.11984 1 5.8V12.2C1 13.8802 1 14.7202 1.32698 15.362C1.6146 15.9265 2.07354 16.3854 2.63803 16.673C3.27976 17 4.11984 17 5.8 17Z"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block w-full overflow-x-auto">
          <table className="w-[97%] mx-auto min-w-[900px]">
            <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
              <tr>
                <th className="text-left py-3 px-5 rounded-tl-[12px]">Buyer</th>
                <th className="text-left py-3 px-5">Listing</th>
                <th className="text-left py-3 px-5">Offer</th>
                <th className="text-left py-3 px-5">Message</th>
                <th className="text-left py-3 px-5">Status</th>
                <th className="text-left py-3 px-5 rounded-tr-[12px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && rows.length === 0 && (
                <tr className="border-t border-gray-100">
                  <td
                    colSpan={6}
                    className="py-8 px-5 text-center text-sm text-gray-500"
                  >
                    Loading requests…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr className="border-t border-gray-100">
                  <td
                    colSpan={6}
                    className="py-3 px-5 text-center text-sm text-gray-600"
                  >
                    No requests found
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-4">
                      {row.buyer.avatar ? (
                        <img
                          src={row.buyer.avatar}
                          alt="avatar"
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-8 h-8 text-xl font-semibold flex items-center justify-center">
                          {row.buyer.name?.charAt(0)}
                        </span>
                      )}
                      <div>
                        <div className="text-esdarkGray opacity-75 text-nav-d">
                          {row.buyer.name}
                        </div>
                        <div className="text-[12px] opacity-50 text-esdarkGray">
                          {row.buyer.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-4">
                      <div className="max-h-[48px] max-w-[48px] bg-gray-100 rounded overflow-hidden">
                        <img
                          src={row.listing.thumb}
                          alt="thumb"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-product-title text-esdarkGray line-clamp-1">
                          {row.listing.title}
                        </div>
                        <div className="text-[14px] opacity-75 text-[#000025]">
                          {row.listing.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-5 text-[#000025] text-page-subtitle-d">
                    {row.offer} €
                  </td>
                  <td className="py-3 px-5 text-tag-d text-[#000025] opacity-50 font-normal max-w-[280px]">
                    {row.message}
                  </td>
                  <td className="py-3 px-5">
                    <span
                      className={`text-[14px] inline-block font-medium px-2.5 py-1 rounded-full ${
                        STATUS_PILL[row.status] || STATUS_PILL.listed
                      }`}
                    >
                      {formatStatusLabel(row.status)}
                    </span>
                  </td>
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-4">
                      {row.status === "pending" && (
                        <>
                          <button
                            onClick={() => handleAcceptRequest(row)}
                            title="Approve"
                            className="h-10 w-10 flex items-center justify-center rounded-[8px] transition-colors bg-[#B9FFCC] text-[#1E1E1E] hover:bg-[#A8F5B8]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="13"
                              viewBox="0 0 18 13"
                              fill="none"
                            >
                              <path
                                d="M17 1L6 12L1 7"
                                stroke="#1E1E1E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeclineRequest(row)}
                            title="Reject"
                            className="h-10 w-10 flex items-center justify-center rounded-[8px] transition-colors bg-[#FFD9D9] text-[#FF6E6E] hover:bg-[#FFC7C7]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M13 1L1 13M1 1L13 13"
                                stroke="#FF6E6E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </>
                      )}
                      <button
                        title="Notify buyer about decision"
                        onClick={() => handleNotifyBuyer(row)}
                        disabled={
                          notifyingBuyerId === row.id ||
                          !["accepted", "rejected"].includes(row.status)
                        }
                        className={`h-10 w-10 flex items-center justify-center border border-gray-200 rounded-[8px] bg-[#FFFFFF] text-[#1E1E1E] transition-opacity ${
                          ["accepted", "rejected"].includes(row.status)
                            ? "opacity-50"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        {notifyingBuyerId === row.id ? (
                          <svg
                            className="animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="#1E1E1E"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="#1E1E1E"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                          </svg>
                        ) : notifiedBuyerId === row.id ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="13"
                            viewBox="0 0 18 13"
                            fill="none"
                          >
                            <path
                              d="M17 1L6 12L1 7"
                              stroke="#1E1E1E"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="18"
                            viewBox="0 0 22 18"
                            fill="none"
                          >
                            <path
                              d="M20.5 15L13.8571 9M8.14286 9L1.50003 15M1 4L9.16492 9.71544C9.82609 10.1783 10.1567 10.4097 10.5163 10.4993C10.8339 10.5785 11.1661 10.5785 11.4837 10.4993C11.8433 10.4097 12.1739 10.1783 12.8351 9.71544L21 4M5.8 17H16.2C17.8802 17 18.7202 17 19.362 16.673C19.9265 16.3854 20.3854 15.9265 20.673 15.362C21 14.7202 21 13.8802 21 12.2V5.8C21 4.11984 21 3.27976 20.673 2.63803C20.3854 2.07354 19.9265 1.6146 19.362 1.32698C18.7202 1 17.8802 1 16.2 1H5.8C4.11984 1 3.27976 1 2.63803 1.32698C2.07354 1.6146 1.6146 2.07354 1.32698 2.63803C1 3.27976 1 4.11984 1 5.8V12.2C1 13.8802 1 14.7202 1.32698 15.362C1.6146 15.9265 2.07354 16.3854 2.63803 16.673C3.27976 17 4.11984 17 5.8 17Z"
                              stroke="#1E1E1E"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accept Request Modal */}
      <AcceptRequestModal
        isOpen={acceptModalOpen}
        onClose={handleCloseAcceptModal}
        onAccept={handleConfirmAccept}
        buyerName={selectedRequest?.buyer?.name || ""}
        itemName={selectedRequest?.listing?.title || ""}
      />

      {/* Decline Request Modal */}
      <DeclineRequestModal
        isOpen={declineModalOpen}
        onClose={handleCloseDeclineModal}
        onDecline={handleConfirmDecline}
        buyerName={selectedRequest?.buyer?.name || ""}
        itemName={selectedRequest?.listing?.title || ""}
      />
    </LayoutBuyerSeller>
  );
};

export default SellerRequests;
